import { Link } from 'react-router-dom';
import { S } from '@/shared/constants/styles';
import { fmtCurrency } from '@/shared/utils/formatCurrency';
import { useHouseholdData } from '@/app/providers/HouseholdDataProvider';
import { DEFAULT_DATA } from '@/shared/constants/config';
import {
  migrateNetWorth,
  computeTotals,
  historySeries,
} from '@/features/networth/utils/netWorthHelpers';

export default function NetWorthSummary({ currency = '$', theme: t }) {
  const { data } = useHouseholdData();
  const safe = data || DEFAULT_DATA;
  const netWorth = migrateNetWorth(safe.netWorth);
  const totals = computeTotals(netWorth, safe.savings?.goals);
  const history = historySeries(netWorth.history);

  const cardBg = t?.cardBg || 'white';
  const cardBorder = t?.cardBorder || '#eee';
  const textColor = t?.text || '#1e293b';
  const textMuted = t?.textMuted || '#94a3b8';

  const hasData = totals.assets.length > 0 || totals.liabilities.length > 0 || totals.totalSavingsGoals > 0;

  if (!hasData) {
    return (
      <div style={{ ...S.card, background: cardBg, borderColor: cardBorder, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: textColor }}>Net Worth</div>
          <Link to="/networth" style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>Set up</Link>
        </div>
        <div style={{ color: textMuted, fontSize: 13, textAlign: 'center', padding: '14px 0' }}>
          Track assets and liabilities to see your total wealth.
        </div>
      </div>
    );
  }

  const netColor = totals.netWorth >= 0 ? '#22c55e' : '#ef4444';

  let deltaNode = null;
  if (history.length >= 2) {
    const prev = history[history.length - 2].netWorth;
    const cur = history[history.length - 1].netWorth;
    const delta = cur - prev;
    const up = delta >= 0;
    const color = up ? '#22c55e' : '#ef4444';
    const arrow = up ? '\u2197' : '\u2198';
    deltaNode = (
      <div style={{ fontSize: 11, color, fontWeight: 700, marginTop: 2 }}>
        {arrow + ' ' + fmtCurrency(Math.abs(delta), currency) + ' vs last month'}
      </div>
    );
  }

  return (
    <div style={{ ...S.card, background: cardBg, borderColor: cardBorder, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: textColor }}>Net Worth</div>
        <Link to="/networth" style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>View details</Link>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 100 }}>
          <div style={{ fontSize: 10, color: textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Assets</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#22c55e', marginTop: 2 }}>{fmtCurrency(totals.totalAssets, currency)}</div>
        </div>
        <div style={{ flex: 1, minWidth: 100 }}>
          <div style={{ fontSize: 10, color: textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Liabilities</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#ef4444', marginTop: 2 }}>{fmtCurrency(totals.totalLiabilities, currency)}</div>
        </div>
        <div style={{ flex: 1.2, minWidth: 120 }}>
          <div style={{ fontSize: 10, color: textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Net Worth</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: netColor, marginTop: 2 }}>{fmtCurrency(totals.netWorth, currency)}</div>
          {deltaNode}
        </div>
      </div>
    </div>
  );
}
